import { Spinner, Button, Alert } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck,
    faTimes,
    faSync,
    faCheckCircle,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FlexStartRow } from '../common/forms/FormElements';

/**
 * Map issue types to user-friendly messages
 */
const ISSUE_MESSAGES = {
    MissingWONumber: 'Could not detect a "WO #" field on this work card.',
    IncompleteWONumber: 'The WO # appears to be incomplete. Work order numbers should be 8 digits.',
    MissingRequiredFields: 'Could not detect a "WO #" field on this work card.',
    NoCardDetected: 'No work card was detected in this image. Please ensure the entire work card is visible.',
    InsufficientText: 'Not enough readable text was found. Please take a clearer photo.',
    SeverelyBlurry: 'The image is too blurry to read. Please hold the camera steady and ensure focus.',
    SlightlyBlurry: 'The image is slightly blurry but may be enhanced.',
    TooDark: 'The image is too dark. Please take the photo in better lighting.',
    TooLight: 'The image is overexposed/washed out. Please reduce lighting or avoid direct light.',
    PoorContrast: 'The image has poor contrast. Please ensure good lighting on the work card.',
    CardCutOff: 'The work card is partially cut off. Please include the entire card in frame.',
    MultipleCards: 'Multiple documents detected. Please photograph only one work card at a time.',
    CardObstructed: 'Something is blocking part of the work card. Please remove any obstructions.',
    LowOcrConfidence: 'The text on the work card is difficult to read. Please take a clearer photo.',
};

/**
 * Get user-friendly message for an issue type
 */
const getIssueMessage = (issueType) => ISSUE_MESSAGES[issueType] || issueType;

/**
 * Retry button shared by the warning/error result states
 */
const RetryButton = ({ onRetry }) =>
    onRetry && (
        <Button
            color="dark"
            size="sm"
            className="mt-2"
            onClick={onRetry}
        >
            <FontAwesomeIcon
                icon={faSync}
                className="mr-1"
            />
            Upload New Image
        </Button>
    );

/**
 * Validation step component - subtle during loading
 */
const ValidationStep = ({ label, status, details }) => {
    const getIcon = () => {
        switch (status) {
            case 'loading':
                return (
                    <Spinner
                        size="sm"
                        color="info"
                        className="mr-2"
                    />
                );
            case 'passed':
                return (
                    <FontAwesomeIcon
                        icon={faCheck}
                        fixedWidth
                        className="mr-2 text-success"
                    />
                );
            case 'failed':
                return (
                    <FontAwesomeIcon
                        icon={faTimes}
                        fixedWidth
                        className="mr-2 text-danger"
                    />
                );
            case 'skipped':
                return <span className="mr-2 text-muted">—</span>;
            default:
                return <span className="mr-2 text-muted">○</span>;
        }
    };

    return (
        <FlexStartRow className="small text-muted mb-1">
            {getIcon()}
            <span>{label}</span>
            {details && <span className="text-muted ml-1">— {details}</span>}
        </FlexStartRow>
    );
};

/**
 * Component to display work card validation status
 */
const WorkCardValidationStatus = ({ status, uploading, onRetry }) => {
    // Uploading state - subtle grey steps
    if (uploading) {
        return (
            <div className="d-flex flex-column mt-2">
                <ValidationStep
                    label="Uploading image"
                    status="loading"
                />
                <ValidationStep
                    label="Checking for required fields"
                    status="pending"
                />
                <ValidationStep
                    label="Analyzing image quality"
                    status="pending"
                />
            </div>
        );
    }

    if (!status) {
        return null;
    }

    const statusName = status.status || status.validationStatusName;
    const issueTypes = status.issueTypes || [];
    const { processingStep } = status;

    // Processing state - show progress through steps based on processingStep
    if (statusName === 'pending' || statusName === 'processing') {
        // Step order matches backend: detection (includes OCR) -> quality -> enhancement -> revalidation
        const getStepStatus = (stepName) => {
            // Map backend steps to UI steps
            // Backend sends: detection, quality, ocr (legacy), enhancement, revalidation
            // UI shows: detection, quality, enhancement, revalidation
            const stepOrder = ['detection', 'quality', 'enhancement', 'revalidation'];

            // Treat 'ocr' as part of 'detection' since OCR happens during document analysis
            const normalizedStep = processingStep === 'ocr' ? 'quality' : processingStep;

            const currentIndex = stepOrder.indexOf(normalizedStep);
            const stepIndex = stepOrder.indexOf(stepName);

            if (currentIndex === -1) {
                // No processingStep yet, show first step as loading
                return stepIndex === 0 ? 'loading' : 'pending';
            }

            if (stepIndex < currentIndex) return 'passed';
            if (stepIndex === currentIndex) return 'loading';
            return 'pending';
        };

        // Check if we're in enhancement phase
        const isEnhancing = processingStep === 'enhancement' || processingStep === 'revalidation';

        return (
            <div className="d-flex flex-column mt-2">
                <ValidationStep
                    label="Upload complete"
                    status="passed"
                />
                <ValidationStep
                    label="Checking for required fields"
                    status={getStepStatus('detection')}
                />
                <ValidationStep
                    label="Analyzing image quality"
                    status={getStepStatus('quality')}
                />
                {isEnhancing && (
                    <>
                        <ValidationStep
                            label="Enhancing image"
                            status={getStepStatus('enhancement')}
                        />
                        {processingStep === 'revalidation' && (
                            <ValidationStep
                                label="Re-validating"
                                status={getStepStatus('revalidation')}
                            />
                        )}
                    </>
                )}
            </div>
        );
    }

    // Valid state - show success result box
    if (statusName === 'valid') {
        return (
            <Alert
                color="success"
                className="mt-2 mb-0"
            >
                <FlexStartRow>
                    <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="mr-2"
                    />
                    <strong>Work card validated successfully</strong>
                </FlexStartRow>
                {status.wasEnhanced && status.enhancementsApplied?.length > 0 && (
                    <p className="small mb-0 mt-2">
                        Image was auto-enhanced: {status.enhancementsApplied.join(', ')}
                    </p>
                )}
            </Alert>
        );
    }

    // Invalid state - show warning result box with issues
    if (statusName === 'invalid') {
        const failureReasons = issueTypes.map((issue) => getIssueMessage(issue));

        return (
            <Alert
                color="warning"
                className="mt-2 mb-0"
            >
                <FlexStartRow>
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="mr-2"
                    />
                    <strong>Validation warnings detected</strong>
                </FlexStartRow>
                {failureReasons.length > 0 && (
                    <ul className="mb-0 mt-2 pl-4">
                        {failureReasons.map((reason, index) => (
                            <li key={index}>{reason}</li>
                        ))}
                    </ul>
                )}
                {status.enhancementAttempted && (
                    <p className="small mb-0 mt-2">
                        We attempted to auto-enhance your image but it still did not meet quality standards.
                    </p>
                )}
                <p className="small font-italic mb-0 mt-2">
                    If you&apos;re confident in the work card image you uploaded, press Save to continue.
                </p>
                <RetryButton onRetry={onRetry} />
            </Alert>
        );
    }

    // Error state - soft messaging so the user can still Save (mirrors the warning treatment)
    if (statusName === 'error') {
        return (
            <Alert
                color="warning"
                className="mt-2 mb-0"
            >
                <FlexStartRow>
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="mr-2"
                    />
                    <strong>Validation warnings detected</strong>
                </FlexStartRow>
                <p className="small mb-0 mt-2">
                    {status.failureReasonMessage || 'We were unable to review your work card. Please try again.'}
                </p>
                <p className="small font-italic mb-0 mt-2">
                    If you&apos;re confident in the work card image you uploaded, press Save to continue.
                </p>
                <RetryButton onRetry={onRetry} />
            </Alert>
        );
    }

    return null;
};

export default WorkCardValidationStatus;
